import type { RangeKey } from '@dataTypes/dashboard.types'

export type TreatmentAcceptanceKpiData = {
  acceptedRate: string
  presentedValue: string
  acceptedValue: string
  sameDayValue: string
  comparisonLabel: string
  acceptedRateDelta: string
  acceptedRateTrend: 'up' | 'down'
  presentedDelta: string
  presentedTrend: 'up' | 'down'
  acceptedDelta: string
  acceptedTrend: 'up' | 'down'
  sameDayDelta: string
  sameDayTrend: 'up' | 'down'
}

const TA_KPI: Record<RangeKey, TreatmentAcceptanceKpiData> = {
  '1D': {
    acceptedRate: '70%',
    presentedValue: '$20K',
    acceptedValue: '$13K',
    sameDayValue: '$5.6K',
    comparisonLabel: 'vs. yesterday',
    acceptedRateDelta: '6%',
    acceptedRateTrend: 'up',
    presentedDelta: '8% ($1,600)',
    presentedTrend: 'up',
    acceptedDelta: '10% ($1,400)',
    acceptedTrend: 'up',
    sameDayDelta: '12% ($672)',
    sameDayTrend: 'up',
  },
  '1W': {
    acceptedRate: '68%',
    presentedValue: '$88K',
    acceptedValue: '$59.8K',
    sameDayValue: '$22.4K',
    comparisonLabel: 'vs. last week',
    acceptedRateDelta: '3%',
    acceptedRateTrend: 'up',
    presentedDelta: '6% ($4,980)',
    presentedTrend: 'up',
    acceptedDelta: '8% ($4,430)',
    acceptedTrend: 'up',
    sameDayDelta: '9% ($1,844)',
    sameDayTrend: 'up',
  },
  '1M': {
    acceptedRate: '65%',
    presentedValue: '$342K',
    acceptedValue: '$222.3K',
    sameDayValue: '$80.2K',
    comparisonLabel: 'vs. last month',
    acceptedRateDelta: '2%',
    acceptedRateTrend: 'up',
    presentedDelta: '5% ($16.3K)',
    presentedTrend: 'up',
    acceptedDelta: '6% ($12.6K)',
    acceptedTrend: 'up',
    sameDayDelta: '7% ($5.2K)',
    sameDayTrend: 'up',
  },
  '1Y': {
    acceptedRate: '63%',
    presentedValue: '$3.8M',
    acceptedValue: '$2.4M',
    sameDayValue: '$840K',
    comparisonLabel: 'vs. last year',
    acceptedRateDelta: '5%',
    acceptedRateTrend: 'up',
    presentedDelta: '11% ($380K)',
    presentedTrend: 'up',
    acceptedDelta: '14% ($296K)',
    acceptedTrend: 'up',
    sameDayDelta: '10% ($76.4K)',
    sameDayTrend: 'up',
  },
  YTD: {
    acceptedRate: '64%',
    presentedValue: '$980K',
    acceptedValue: '$627.2K',
    sameDayValue: '$214.4K',
    comparisonLabel: 'vs. YTD last year',
    acceptedRateDelta: '4%',
    acceptedRateTrend: 'up',
    presentedDelta: '9% ($80.8K)',
    presentedTrend: 'up',
    acceptedDelta: '11% ($62.7K)',
    acceptedTrend: 'up',
    sameDayDelta: '8% ($15.9K)',
    sameDayTrend: 'up',
  },
}

export function getTreatmentAcceptanceKpi(
  range: RangeKey,
): TreatmentAcceptanceKpiData {
  return TA_KPI[range]
}

/** Parses KPI strings like `70%` to a number for charts (0–100). */
export function parseAcceptedRatePercent(s: string): number {
  return parseFloat(String(s).replace(/%/g, '').trim()) || 0
}

export type TreatmentAcceptanceChartPoint = {
  label: string
  presented: number
  accepted: number
}

export type TreatmentAcceptanceChartConfig = {
  data: TreatmentAcceptanceChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

const TA_CHART: Record<RangeKey, TreatmentAcceptanceChartConfig> = {
  '1D': {
    data: [
      { label: '8 AM', presented: 5, accepted: 2.1 },
      { label: '9 AM', presented: 2, accepted: 5.6 },
      { label: '10 AM', presented: 4.8, accepted: 4.8 },
      { label: '11 AM', presented: 6.5, accepted: 5.2 },
      { label: '12 PM', presented: 4, accepted: 1.6 },
      { label: '1 PM', presented: 3.2, accepted: 5.6 },
      { label: '2 PM', presented: 2, accepted: 2.6 },
      { label: '3 PM', presented: 5.8, accepted: 5.8 },
      { label: '4 PM', presented: 7, accepted: 2.9 },
      { label: '5 PM', presented: 7, accepted: 2.9 },
    ],
    yAxisDomain: [0, 10],
    yAxisTicks: [2, 4, 6, 8, 10],
  },
  '1W': {
    data: [
      { label: 'Mon', presented: 16, accepted: 11 },
      { label: 'Tue', presented: 20, accepted: 14 },
      { label: 'Wed', presented: 18, accepted: 12 },
      { label: 'Thu', presented: 17, accepted: 11 },
      { label: 'Fri', presented: 17, accepted: 12 },
    ],
    yAxisDomain: [0, 25],
    yAxisTicks: [0, 5, 10, 15, 20, 25],
  },
  '1M': {
    data: [
      { label: 'Wk 1', presented: 84, accepted: 55 },
      { label: 'Wk 2', presented: 90, accepted: 58 },
      { label: 'Wk 3', presented: 82, accepted: 54 },
      { label: 'Wk 4', presented: 86, accepted: 56 },
    ],
    yAxisDomain: [0, 100],
    yAxisTicks: [0, 25, 50, 75, 100],
  },
  '1Y': {
    data: [
      { label: 'Apr', presented: 298, accepted: 186 },
      { label: 'May', presented: 310, accepted: 195 },
      { label: 'Jun', presented: 322, accepted: 206 },
      { label: 'Jul', presented: 330, accepted: 211 },
      { label: 'Aug', presented: 318, accepted: 201 },
      { label: 'Sep', presented: 305, accepted: 192 },
      { label: 'Oct', presented: 312, accepted: 197 },
      { label: 'Nov', presented: 328, accepted: 207 },
      { label: 'Dec', presented: 302, accepted: 190 },
      { label: 'Jan', presented: 316, accepted: 198 },
      { label: 'Feb', presented: 328, accepted: 210 },
      { label: 'Mar', presented: 336, accepted: 219 },
    ],
    yAxisDomain: [0, 400],
    yAxisTicks: [0, 100, 200, 300, 400],
  },
  YTD: {
    data: [
      { label: 'Jan', presented: 316, accepted: 198 },
      { label: 'Feb', presented: 328, accepted: 210 },
      { label: 'Mar', presented: 336, accepted: 219 },
    ],
    yAxisDomain: [0, 400],
    yAxisTicks: [0, 100, 200, 300, 400],
  },
}

export function getTreatmentAcceptanceChartConfig(
  range: RangeKey,
): TreatmentAcceptanceChartConfig {
  return TA_CHART[range]
}

export type TreatmentAcceptanceByProviderRow = {
  id: string
  provider: string
  presented: string
  accepted: string
  acceptanceRatePct: string
  sameDayAcceptance: string
  followUpAcceptance: string
}

const BY_PROVIDER: Record<RangeKey, TreatmentAcceptanceByProviderRow[]> = {
  '1D': [
    {
      id: '1',
      provider: 'Dr. Toby Nolan',
      presented: '$8,400',
      accepted: '$5,880',
      acceptanceRatePct: '70%',
      sameDayAcceptance: '75%',
      followUpAcceptance: '45%',
    },
    {
      id: '2',
      provider: 'Dr. Clara Bennett',
      presented: '$6,000',
      accepted: '$4,500',
      acceptanceRatePct: '75%',
      sameDayAcceptance: '68%',
      followUpAcceptance: '38%',
    },
    {
      id: '3',
      provider: 'Dr. Juan Ramirez',
      presented: '$3,200',
      accepted: '$1,856',
      acceptanceRatePct: '58%',
      sameDayAcceptance: '52%',
      followUpAcceptance: '30%',
    },
    {
      id: '4',
      provider: 'Dr. Lisa Chen',
      presented: '$2,400',
      accepted: '$1,536',
      acceptanceRatePct: '64%',
      sameDayAcceptance: '60%',
      followUpAcceptance: '35%',
    },
  ],
  '1W': [
    {
      id: '1',
      provider: 'Dr. Toby Nolan',
      presented: '$34,200',
      accepted: '$24,624',
      acceptanceRatePct: '72%',
      sameDayAcceptance: '74%',
      followUpAcceptance: '44%',
    },
    {
      id: '2',
      provider: 'Dr. Clara Bennett',
      presented: '$26,400',
      accepted: '$19,536',
      acceptanceRatePct: '74%',
      sameDayAcceptance: '67%',
      followUpAcceptance: '37%',
    },
    {
      id: '3',
      provider: 'Dr. Juan Ramirez',
      presented: '$14,800',
      accepted: '$8,584',
      acceptanceRatePct: '58%',
      sameDayAcceptance: '51%',
      followUpAcceptance: '29%',
    },
    {
      id: '4',
      provider: 'Dr. Lisa Chen',
      presented: '$12,600',
      accepted: '$7,812',
      acceptanceRatePct: '62%',
      sameDayAcceptance: '58%',
      followUpAcceptance: '32%',
    },
  ],
  '1M': [
    {
      id: '1',
      provider: 'Dr. Toby Nolan',
      presented: '$128,400',
      accepted: '$89,880',
      acceptanceRatePct: '70%',
      sameDayAcceptance: '73%',
      followUpAcceptance: '43%',
    },
    {
      id: '2',
      provider: 'Dr. Clara Bennett',
      presented: '$104,600',
      accepted: '$74,266',
      acceptanceRatePct: '71%',
      sameDayAcceptance: '66%',
      followUpAcceptance: '36%',
    },
    {
      id: '3',
      provider: 'Dr. Juan Ramirez',
      presented: '$62,400',
      accepted: '$34,320',
      acceptanceRatePct: '55%',
      sameDayAcceptance: '50%',
      followUpAcceptance: '28%',
    },
    {
      id: '4',
      provider: 'Dr. Lisa Chen',
      presented: '$46,600',
      accepted: '$27,894',
      acceptanceRatePct: '60%',
      sameDayAcceptance: '55%',
      followUpAcceptance: '30%',
    },
  ],
  '1Y': [
    {
      id: '1',
      provider: 'Dr. Toby Nolan',
      presented: '$1,440,000',
      accepted: '$993,600',
      acceptanceRatePct: '69%',
      sameDayAcceptance: '72%',
      followUpAcceptance: '42%',
    },
    {
      id: '2',
      provider: 'Dr. Clara Bennett',
      presented: '$1,188,000',
      accepted: '$820,620',
      acceptanceRatePct: '69%',
      sameDayAcceptance: '65%',
      followUpAcceptance: '35%',
    },
    {
      id: '3',
      provider: 'Dr. Juan Ramirez',
      presented: '$720,000',
      accepted: '$374,400',
      acceptanceRatePct: '52%',
      sameDayAcceptance: '48%',
      followUpAcceptance: '26%',
    },
    {
      id: '4',
      provider: 'Dr. Lisa Chen',
      presented: '$452,000',
      accepted: '$262,760',
      acceptanceRatePct: '58%',
      sameDayAcceptance: '53%',
      followUpAcceptance: '28%',
    },
  ],
  YTD: [
    {
      id: '1',
      provider: 'Dr. Toby Nolan',
      presented: '$374,400',
      accepted: '$258,336',
      acceptanceRatePct: '69%',
      sameDayAcceptance: '72%',
      followUpAcceptance: '42%',
    },
    {
      id: '2',
      provider: 'Dr. Clara Bennett',
      presented: '$309,600',
      accepted: '$213,624',
      acceptanceRatePct: '69%',
      sameDayAcceptance: '65%',
      followUpAcceptance: '35%',
    },
    {
      id: '3',
      provider: 'Dr. Juan Ramirez',
      presented: '$184,800',
      accepted: '$96,096',
      acceptanceRatePct: '52%',
      sameDayAcceptance: '48%',
      followUpAcceptance: '26%',
    },
    {
      id: '4',
      provider: 'Dr. Lisa Chen',
      presented: '$110,400',
      accepted: '$64,032',
      acceptanceRatePct: '58%',
      sameDayAcceptance: '53%',
      followUpAcceptance: '28%',
    },
  ],
}

export function getTreatmentAcceptanceByProviderRows(
  range: RangeKey,
): TreatmentAcceptanceByProviderRow[] {
  return BY_PROVIDER[range]
}

/* ─── By treatment category ───────────────────────────────────────────────── */

export type TreatmentAcceptanceByCategoryRow = {
  id: string
  category: string
  presented: string
  accepted: string
  acceptanceRate: string
  avgCaseValue: string
}

const BY_CATEGORY: Record<RangeKey, TreatmentAcceptanceByCategoryRow[]> = {
  '1D': [
    {
      id: '1',
      category: 'Restorative',
      presented: '$6,400',
      accepted: '$4,864',
      acceptanceRate: '76%',
      avgCaseValue: '$1,216',
    },
    {
      id: '2',
      category: 'Periodontal',
      presented: '$4,200',
      accepted: '$2,772',
      acceptanceRate: '66%',
      avgCaseValue: '$924',
    },
    {
      id: '3',
      category: 'Endodontics',
      presented: '$3,800',
      accepted: '$2,546',
      acceptanceRate: '67%',
      avgCaseValue: '$1,273',
    },
    {
      id: '4',
      category: 'Oral Surgery',
      presented: '$3,200',
      accepted: '$2,080',
      acceptanceRate: '65%',
      avgCaseValue: '$1,040',
    },
    {
      id: '5',
      category: 'Orthodontics',
      presented: '$2,400',
      accepted: '$1,920',
      acceptanceRate: '80%',
      avgCaseValue: '$4,800',
    },
  ],
  '1W': [
    {
      id: '1',
      category: 'Restorative',
      presented: '$28,400',
      accepted: '$21,584',
      acceptanceRate: '76%',
      avgCaseValue: '$1,244',
    },
    {
      id: '2',
      category: 'Periodontal',
      presented: '$18,600',
      accepted: '$12,276',
      acceptanceRate: '66%',
      avgCaseValue: '$940',
    },
    {
      id: '3',
      category: 'Endodontics',
      presented: '$16,400',
      accepted: '$10,988',
      acceptanceRate: '67%',
      avgCaseValue: '$1,283',
    },
    {
      id: '4',
      category: 'Oral Surgery',
      presented: '$14,200',
      accepted: '$9,230',
      acceptanceRate: '65%',
      avgCaseValue: '$1,050',
    },
    {
      id: '5',
      category: 'Orthodontics',
      presented: '$10,400',
      accepted: '$8,320',
      acceptanceRate: '80%',
      avgCaseValue: '$4,800',
    },
  ],
  '1M': [
    {
      id: '1',
      category: 'Restorative',
      presented: '$108,400',
      accepted: '$82,384',
      acceptanceRate: '76%',
      avgCaseValue: '$1,285',
    },
    {
      id: '2',
      category: 'Periodontal',
      presented: '$72,600',
      accepted: '$47,916',
      acceptanceRate: '66%',
      avgCaseValue: '$960',
    },
    {
      id: '3',
      category: 'Endodontics',
      presented: '$64,200',
      accepted: '$43,014',
      acceptanceRate: '67%',
      avgCaseValue: '$1,293',
    },
    {
      id: '4',
      category: 'Oral Surgery',
      presented: '$56,800',
      accepted: '$36,920',
      acceptanceRate: '65%',
      avgCaseValue: '$1,058',
    },
    {
      id: '5',
      category: 'Orthodontics',
      presented: '$40,000',
      accepted: '$32,000',
      acceptanceRate: '80%',
      avgCaseValue: '$5,000',
    },
  ],
  '1Y': [
    {
      id: '1',
      category: 'Restorative',
      presented: '$1,188,000',
      accepted: '$903,280',
      acceptanceRate: '76%',
      avgCaseValue: '$1,302',
    },
    {
      id: '2',
      category: 'Periodontal',
      presented: '$820,000',
      accepted: '$541,200',
      acceptanceRate: '66%',
      avgCaseValue: '$978',
    },
    {
      id: '3',
      category: 'Endodontics',
      presented: '$720,000',
      accepted: '$482,400',
      acceptanceRate: '67%',
      avgCaseValue: '$1,314',
    },
    {
      id: '4',
      category: 'Oral Surgery',
      presented: '$640,000',
      accepted: '$416,000',
      acceptanceRate: '65%',
      avgCaseValue: '$1,080',
    },
    {
      id: '5',
      category: 'Orthodontics',
      presented: '$432,000',
      accepted: '$345,600',
      acceptanceRate: '80%',
      avgCaseValue: '$5,200',
    },
  ],
  YTD: [
    {
      id: '1',
      category: 'Restorative',
      presented: '$306,000',
      accepted: '$232,560',
      acceptanceRate: '76%',
      avgCaseValue: '$1,296',
    },
    {
      id: '2',
      category: 'Periodontal',
      presented: '$212,000',
      accepted: '$139,920',
      acceptanceRate: '66%',
      avgCaseValue: '$968',
    },
    {
      id: '3',
      category: 'Endodontics',
      presented: '$186,000',
      accepted: '$124,620',
      acceptanceRate: '67%',
      avgCaseValue: '$1,302',
    },
    {
      id: '4',
      category: 'Oral Surgery',
      presented: '$164,000',
      accepted: '$106,600',
      acceptanceRate: '65%',
      avgCaseValue: '$1,065',
    },
    {
      id: '5',
      category: 'Orthodontics',
      presented: '$112,000',
      accepted: '$89,600',
      acceptanceRate: '80%',
      avgCaseValue: '$5,100',
    },
  ],
}

export function getTreatmentAcceptanceByCategoryRows(
  range: RangeKey,
): TreatmentAcceptanceByCategoryRow[] {
  return BY_CATEGORY[range]
}

/* ─── By time period ──────────────────────────────────────────────────────── */

export type TreatmentAcceptanceByTimePeriodRow = {
  id: string
  timePeriod: string
  presented: string
  accepted: string
  acceptanceRate: string
}

const BY_TIME_PERIOD: Record<RangeKey, TreatmentAcceptanceByTimePeriodRow[]> = {
  '1D': [
    {
      id: '1',
      timePeriod: 'Today',
      presented: '$20K',
      accepted: '$14K',
      acceptanceRate: '70%',
    },
    {
      id: '2',
      timePeriod: 'Yesterday',
      presented: '$18.5K',
      accepted: '$11.8K',
      acceptanceRate: '64%',
    },
    {
      id: '3',
      timePeriod: 'Last week same day',
      presented: '$19.2K',
      accepted: '$12.5K',
      acceptanceRate: '65%',
    },
  ],
  '1W': [
    {
      id: '1',
      timePeriod: 'This week',
      presented: '$88K',
      accepted: '$59.8K',
      acceptanceRate: '68%',
    },
    {
      id: '2',
      timePeriod: 'Last week',
      presented: '$82.8K',
      accepted: '$54.6K',
      acceptanceRate: '66%',
    },
    {
      id: '3',
      timePeriod: 'Last month same week',
      presented: '$80.4K',
      accepted: '$52.3K',
      acceptanceRate: '65%',
    },
  ],
  '1M': [
    {
      id: '1',
      timePeriod: 'This month',
      presented: '$342K',
      accepted: '$222.3K',
      acceptanceRate: '65%',
    },
    {
      id: '2',
      timePeriod: 'Last month',
      presented: '$326K',
      accepted: '$205.4K',
      acceptanceRate: '63%',
    },
    {
      id: '3',
      timePeriod: 'Last quarter',
      presented: '$960K',
      accepted: '$595.2K',
      acceptanceRate: '62%',
    },
  ],
  '1Y': [
    {
      id: '1',
      timePeriod: 'This year',
      presented: '$3.8M',
      accepted: '$2.4M',
      acceptanceRate: '63%',
    },
    {
      id: '2',
      timePeriod: 'Last year',
      presented: '$3.42M',
      accepted: '$2.05M',
      acceptanceRate: '60%',
    },
    {
      id: '3',
      timePeriod: '2 years ago',
      presented: '$3.1M',
      accepted: '$1.8M',
      acceptanceRate: '58%',
    },
  ],
  YTD: [
    {
      id: '1',
      timePeriod: 'Jan 2026',
      presented: '$316K',
      accepted: '$198K',
      acceptanceRate: '63%',
    },
    {
      id: '2',
      timePeriod: 'Feb 2026',
      presented: '$328K',
      accepted: '$210K',
      acceptanceRate: '64%',
    },
    {
      id: '3',
      timePeriod: 'Mar 2026',
      presented: '$336K',
      accepted: '$219K',
      acceptanceRate: '65%',
    },
  ],
}

export function getTreatmentAcceptanceByTimePeriodRows(
  range: RangeKey,
): TreatmentAcceptanceByTimePeriodRow[] {
  return BY_TIME_PERIOD[range]
}
